import React from 'react';
import logoImg from '@/assets/logo.png';
import logoWithTextImg from '@/assets/logo-with-text.png';

export interface SystemLogoProps {
  /** 容器额外 className */
  className?: string;
  /** 图标/图片尺寸 className，默认为 'h-8 w-auto' (带文字模式) 或 'w-8 h-8' (纯图标模式) */
  sizeClassName?: string;
  /** 图标/图片额外 className */
  iconClassName?: string;
  /** 文字额外 className */
  textClassName?: string;
  /** 是否使用带文字的 Logo 图片，默认为 true */
  useTextLogo?: boolean;
  /** 是否显示品牌名称，默认 true */
  showText?: boolean;
  /** 自定义系统名称，默认 '灵搭岛' */
  title?: string;
}

/**
 * 系统统一 Logo 组件
 */
export const SystemLogo: React.FC<SystemLogoProps> = ({
  className = '',
  sizeClassName,
  iconClassName = '',
  textClassName = '',
  useTextLogo = true,
  showText = true,
  title = '灵搭岛',
}) => {
  // 如果 showText 为 false，说明只需要纯 Icon 模式；否则默认使用带文字的 Logo 图片
  const isWithText = useTextLogo && showText;

  if (isWithText) {
    const defaultSize = sizeClassName || 'h-8 sm:h-9 xl:h-10 2xl:h-12 3xl:h-14 4k:h-16 w-auto';
    // 如果 textClassName 中包含 text-white，说明在深色背景上使用，添加反色效果以显示白色 Logo
    const isDarkBg = textClassName.includes('text-white');
    const darkFilter = isDarkBg ? 'brightness-0 invert' : '';

    return (
      <div className={`flex items-center ${className}`}>
        <img
          src={logoWithTextImg}
          alt={title}
          className={`${defaultSize} object-contain ${darkFilter} ${iconClassName}`}
        />
      </div>
    );
  }

  // 纯图标模式 / 图标 + 独立 HTML 文字模式
  const defaultSize = sizeClassName || 'w-8 h-8 xl:w-10 xl:h-10 2xl:w-12 2xl:h-12 3xl:w-14 3xl:h-14';
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <img
        src={logoImg}
        alt={title}
        className={`${defaultSize} object-contain ${iconClassName}`}
      />
      {showText && (
        <span
          className={`font-heading font-semibold text-base tracking-tight text-foreground-950 ${textClassName}`}
        >
          {title}
        </span>
      )}
    </div>
  );
};

export default SystemLogo;
